import React from 'react';
import { useTranslation } from 'react-i18next';
import { RoundEnd } from '../../models/RoundEnd';
import { Team } from '../../models/Team';
import { ctWonRound, team1IsCtInRound, teamWonRound } from './matchStats';
import { CT_COLOR, T_COLOR, roundEndIcon } from './roundEndIcon';
import styles from './MatchPage2.module.css';

interface Match2RoundsProps {
    // Needed to spot a stale round-end winner id (see teamWonRound).
    team2Id?: string;
    roundEnds: { [round: number]: RoundEnd };
    team1: Team;
    // Live maps: draw every regulation round from the start (unplayed ones as a dim number) so
    // the strip keeps its full width and halftime divider instead of stretching a few rounds.
    padToRegulation?: boolean;
    // Live maps: the round being played, whose number is picked out in orange.
    currentRound?: number;
    // Set when the map has a 2D replay: a played round then opens there.
    onRoundClick?: (round: number) => void;
}

// Round-history strip: one column per round with the winner's icon in the top
// (team1) or bottom (team2) row and a halftime divider before round 13. Always a
// single line — OT rounds are cut off at 12-12 like the old design (never wrap).
const REGULATION_ROUNDS = 24;

const Match2Rounds: React.FC<Match2RoundsProps> = ({ roundEnds, team1, team2Id, padToRegulation = false, currentRound, onRoundClick }) => {
    const { t } = useTranslation('pages');

    const rounds = Object.entries(roundEnds)
        .map(([round, data]) => ({ round: parseInt(round), ...data }))
        .sort((a, b) => a.round - b.round)
        .filter(r => r.round <= REGULATION_ROUNDS);

    if (rounds.length === 0 && !padToRegulation) return null;

    // One slot per round: the played ones, plus (live) every regulation round not played yet.
    const byRound = new Map(rounds.map(r => [r.round, r]));
    const slots = padToRegulation
        ? Array.from({ length: REGULATION_ROUNDS }, (_, i) => ({ round: i + 1, data: byRound.get(i + 1) }))
        : rounds.map(r => ({ round: r.round, data: r }));

    return (
        <div className={styles.rounds}>
            <div className={styles.roundsInner}>
                {slots.map(({ round, data }) => {
                    const colClass = `${styles.roundCol} ${round === 13 ? styles.roundColDivider : ''} ${data ? '' : styles.roundColPending}`;
                    const number = (
                        <div className={`${styles.roundNum} ${round === currentRound ? styles.roundNumCurrent : ''}`}>{round}</div>
                    );
                    if (!data) {
                        return (
                            <div key={round} className={colClass}>
                                <div className={styles.roundSlot} />
                                {number}
                                <div className={styles.roundSlot} />
                            </div>
                        );
                    }
                    const team1Won = teamWonRound(data.reason, data.teamWonID, team1.teamId, team2Id, team1IsCtInRound(round));
                    const color = ctWonRound(data.reason) ? CT_COLOR : T_COLOR;
                    const icon = roundEndIcon(data.reason);
                    const circle = (
                        <span className={styles.roundCircle}>
                            <span
                                className={styles.roundIcon}
                                title={`${t('match2.round')} ${round}: ${data.reason}`}
                                style={{
                                    backgroundColor: color,
                                    WebkitMask: `url(${icon}) center / contain no-repeat`,
                                    mask: `url(${icon}) center / contain no-repeat`,
                                }}
                            />
                        </span>
                    );
                    const clickProps = onRoundClick ? {
                        role: 'button',
                        tabIndex: 0,
                        title: t('match2.openRoundInReplay', { round }),
                        onClick: () => onRoundClick(round),
                        onKeyDown: (e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onRoundClick(round);
                            }
                        },
                    } : {};
                    return (
                        <div key={round} className={`${colClass} ${onRoundClick ? styles.roundColClickable : ''}`} {...clickProps}>
                            <div className={styles.roundSlot}>{team1Won && circle}</div>
                            {number}
                            <div className={styles.roundSlot}>{!team1Won && circle}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default Match2Rounds;
